import type { MutableRefObject } from 'react';

type PushToDataLayerParams = {
  id: string;
  step: string;
  parameters?: string;
};

/**
 * Pushes analytics events to the dataLayer
 * @param params - Event parameters
 */
export const pushToDataLayer = ({ id, step, parameters }: PushToDataLayerParams): void => {
  window?.dataLayer?.push({
    event: 'process',
    process__name: id,
    process__step: step,
    ...(parameters && { process__parameters: parameters }),
  });
};

type TrackProcessAbandonParams = {
  id: string;
  success: boolean;
  submissionError: { title?: string; description?: string } | undefined;
  trackedFields: MutableRefObject<Set<string>>;
};

/**
 * Tracks form abandonment for analytics
 * @param params - Tracking parameters
 */
export const trackProcessAbandon = ({
  id,
  success,
  submissionError,
  trackedFields,
}: TrackProcessAbandonParams): void => {
  if (!success && !submissionError) {
    const lastInteractedField = [...trackedFields.current].pop();
    pushToDataLayer({ id, step: 'abandon', parameters: lastInteractedField });
  }
};

type TrackFieldInteractionParams = {
  id: string;
  watchedValues: Record<string, unknown>;
  trackedFields: MutableRefObject<Set<string>>;
};

/**
 * Tracks field interactions for analytics
 * @param params - Tracking parameters
 */
export const trackFieldInteraction = ({
  id,
  watchedValues,
  trackedFields,
}: TrackFieldInteractionParams): void => {
  Object.keys(watchedValues).forEach((field) => {
    if (watchedValues[field] && !trackedFields?.current?.has(field)) {
      pushToDataLayer({ id, step: 'selection', parameters: field });
      trackedFields?.current.add(field);
    }
  });
};

// Extend Window interface for dataLayer
declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}
